import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";

/**
 * Workshop: Add Agent panel — floating panel with a grid of agent templates.
 */

interface AgentTemplate {
  id: string;
  emoji: string;
  name: string;
  description: string;
}

interface AddAgentPanelProps {
  templates?: AgentTemplate[];
  onSelect?: (templateId: string) => void;
  onClose?: () => void;
  className?: string;
  "data-testid"?: string;
}

const DEFAULT_TEMPLATES: AgentTemplate[] = [
  { id: "coder", emoji: "\u{1F4BB}", name: "Coder", description: "Write & debug code" },
  { id: "researcher", emoji: "\u{1F52C}", name: "Researcher", description: "Search & analyze" },
  { id: "writer", emoji: "\u270D\uFE0F", name: "Writer", description: "Draft & edit content" },
  { id: "analyst", emoji: "\u{1F4CA}", name: "Analyst", description: "Data & insights" },
];

export function AddAgentPanel({
  templates = DEFAULT_TEMPLATES,
  onSelect,
  onClose,
  className,
  "data-testid": testId = "add-agent-panel",
}: AddAgentPanelProps) {
  return (
    <div
      className={cn(
        "flex w-[260px] flex-col rounded-xl border border-glass-stroke bg-glass-fill p-3 shadow-glass backdrop-blur-glass-md",
        className,
      )}
      data-testid={testId}
    >
      {/* Header */}
      <div className="flex items-center justify-between pb-2">
        <span className="text-xs font-medium text-primary">Add Agent</span>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-muted transition-colors hover:text-primary"
          data-testid={`${testId}-close`}
        >
          <Icons.X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Template grid */}
      <div className="grid grid-cols-2 gap-2">
        {templates.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            onClick={() => onSelect?.(tpl.id)}
            className="flex flex-col items-center gap-1 rounded-lg border border-glass-stroke-subtle p-3 transition-colors hover:border-glass-stroke hover:bg-elevated"
            data-testid={`${testId}-template-${tpl.id}`}
          >
            <span className="text-[28px]" aria-hidden="true">
              {tpl.emoji}
            </span>
            <span className="text-xs font-medium text-primary">{tpl.name}</span>
            <span className="text-[10px] text-muted">{tpl.description}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export type { AddAgentPanelProps, AgentTemplate };
